import React from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';

import { REDACTED_FIELD } from '../../../config/logrocket';
import {
  getDashboardPanelTokens,
  rem,
  TILE_SUBTITLE_FONT_SIZE,
  TILE_SUBTITLE_GAP,
} from './dashboardPanelTokens';
import type { ClaimSummary } from './latestOpenClaim';
import { TileHeading } from './TileHeading';

interface LatestClaimPanelProps {
  readonly claim: ClaimSummary | null;
  readonly phone: string;
  readonly email: string;
  readonly isLoading?: boolean;
  readonly isError?: boolean;
  /** Destination for the header link; omitted hides the link. */
  readonly allClaimsHref?: string;
}

/**
 * The claims tile: the open claim the insured most recently saw movement on,
 * above the contact details for reporting a new one. With no open claim the
 * tile is just the reporting details, which is all it has ever shown.
 */
export function LatestClaimPanel({
  claim,
  phone,
  email,
  isLoading = false,
  isError = false,
  allClaimsHref,
}: Readonly<LatestClaimPanelProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  return (
    // The dashboard wraps some tiles in a flex Paper, where a plain block
    // child collapses to its content width instead of filling the card.
    <Box data-qa="CLAIMS_PANEL" sx={{ width: '100%', minWidth: 0 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={rem(12)}
        pb={TILE_SUBTITLE_GAP}
      >
        <TileHeading>Claims</TileHeading>
        {allClaimsHref ? (
          <Typography
            component={Link}
            to={allClaimsHref}
            sx={{
              flexShrink: 0,
              fontSize: rem(13.5),
              fontWeight: 600,
              color: tokens.brand,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            All claims
          </Typography>
        ) : null}
      </Box>

      <ClaimBody claim={claim} isLoading={isLoading} isError={isError} />

      <Box
        sx={{
          borderTop: `1px solid ${tokens.line}`,
          marginTop: rem(14),
          paddingTop: rem(12),
        }}
      >
        <Typography
          sx={{
            fontSize: rem(10.5),
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: tokens.inkFaint,
          }}
        >
          Report a new claim
        </Typography>
        <ContactLine href={`tel:${phone}`} icon={<PhoneInTalkOutlinedIcon />}>
          {phone}
        </ContactLine>
        <ContactLine href={`mailto:${email}`} icon={<MailOutlineIcon />}>
          {email}
        </ContactLine>
      </Box>
    </Box>
  );
}

/**
 * A failed fetch and an account with nothing open mean different things, so
 * they never share a message: "no open claims" would be a claim about the
 * account that we cannot actually make when the request failed.
 */
function ClaimBody({
  claim,
  isLoading,
  isError,
}: Readonly<{
  claim: ClaimSummary | null;
  isLoading: boolean;
  isError: boolean;
}>) {
  if (isLoading) {
    return <ClaimSkeleton />;
  }
  if (isError) {
    return (
      <ClaimMessage text="Your claims could not be loaded right now. Please try again in a few minutes." />
    );
  }
  return <OpenClaim claim={claim} />;
}

function ClaimMessage({ text }: Readonly<{ text: string }>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  return (
    <Typography
      sx={{ fontSize: TILE_SUBTITLE_FONT_SIZE, color: tokens.inkFaint }}
    >
      {text}
    </Typography>
  );
}

function OpenClaim({ claim }: Readonly<{ claim: ClaimSummary | null }>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  if (!claim) {
    return <ClaimMessage text="No open claims." />;
  }

  return (
    <Box
      data-qa="CLAIMS_OPEN_CLAIM"
      sx={{
        backgroundColor: tokens.surfaceMuted,
        borderRadius: rem(8),
        padding: rem(14),
      }}
    >
      {/* One line: the claim number is an identifier and never breaks
          mid-token, so the chip gives up width first and truncates its own
          label rather than pushing the number onto a second line. */}
      <Box
        display="flex"
        alignItems="baseline"
        justifyContent="space-between"
        gap={rem(8)}
      >
        <Typography
          sx={{
            fontSize: rem(14),
            fontWeight: 700,
            lineHeight: 1.3,
            color: tokens.ink,
            whiteSpace: 'nowrap',
          }}
        >
          {claim.claimNumber}
        </Typography>
        {claim.statusLabel ? (
          <Typography
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: rem(10.5),
              fontWeight: 600,
              letterSpacing: '0.01em',
              color: tokens.brand,
              backgroundColor: tokens.brandSurface,
              borderRadius: rem(999),
              padding: `${rem(3)} ${rem(8)}`,
            }}
          >
            {claim.statusLabel}
          </Typography>
        ) : null}
      </Box>

      {claim.description ? (
        <Typography
          className={REDACTED_FIELD}
          sx={{
            marginTop: rem(6),
            fontSize: rem(12.5),
            lineHeight: 1.4,
            color: tokens.inkBody,
          }}
        >
          {claim.description}
        </Typography>
      ) : null}

      <Box mt={rem(10)}>
        <ClaimFact label="Filed" value={claim.filedOn} />
        <ClaimFact label="Last update" value={claim.lastUpdatedOn} />
      </Box>

      {claim.handler ? (
        <Typography
          className={REDACTED_FIELD}
          sx={{
            marginTop: rem(8),
            fontSize: rem(11),
            color: tokens.inkFaint,
          }}
        >
          Handler: {claim.handler}
        </Typography>
      ) : null}
    </Box>
  );
}

/** A label/value pair: the label recedes so the date is what gets scanned. */
function ClaimFact({
  label,
  value,
}: Readonly<{ label: string; value: string }>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  if (!value) {
    return null;
  }

  return (
    <Typography
      sx={{ fontSize: rem(11.5), lineHeight: 1.6, color: tokens.inkFaint }}
    >
      {label}{' '}
      <Box component="span" sx={{ fontWeight: 600, color: tokens.inkBody }}>
        {value}
      </Box>
    </Typography>
  );
}

function ContactLine({
  href,
  icon,
  children,
}: Readonly<{
  href: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  return (
    <Box
      component="a"
      href={href}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: rem(8),
        marginTop: rem(8),
        fontSize: rem(13),
        fontWeight: 600,
        color: tokens.ink,
        textDecoration: 'none',
        '& svg': { fontSize: rem(15), color: tokens.inkFaint },
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {icon}
      {children}
    </Box>
  );
}

function ClaimSkeleton() {
  return (
    <Box data-qa="CLAIMS_PANEL_LOADING">
      <Skeleton
        variant="rectangular"
        sx={{ height: rem(112), borderRadius: rem(8) }}
      />
    </Box>
  );
}

export default LatestClaimPanel;
